import { useState } from 'react';
import { Link } from 'react-router';

import { useCan } from '@/shared/session/profiles';
import type { Supplier } from '@/shared/tariffs/types';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Card } from '@/shared/ui/Card';
import { CardList, CardListItem } from '@/shared/ui/CardList';
import { Dialog } from '@/shared/ui/Dialog';
import { ErrorMessage } from '@/shared/ui/ErrorMessage';
import { Input } from '@/shared/ui/Input';
import { Spinner } from '@/shared/ui/Spinner';
import { FileTextIcon, SearchIcon, UploadIcon } from '@/shared/ui/icons';
import { formatBytes } from '@/shared/utils/format';

import { isAbort } from './api';
import {
  CATALOG_ACCEPT,
  CATALOG_STATUS_LABELS,
  type Catalog,
  catalogFacts,
  catalogFailureTitle,
  catalogPath,
  isPdfFile,
  nameFromFile,
} from './types';
import {
  useCatalogs,
  useDeleteCatalog,
  useRenameCatalog,
  useRetryCatalog,
  useUploadCatalog,
} from './useCatalogs';

/** What the back takes (`CATALOG_MAX_UPLOAD_BYTES`); the link it signs says the same in
 * `max_bytes`, but the dialog has to say it before asking for one. */
const MAX_BYTES = 500 * 1024 * 1024;

/**
 * The catalogs of a supplier (contract v10.49): open one, and for whoever manages the
 * documents, upload, rename, delete and ask again for one that got stuck. The PDF goes from
 * the browser to the bucket and never through the API (ADR 0044 of the back).
 */
export function CatalogsCard({ supplier }: { supplier: Supplier }) {
  const manage = useCan('manage_documents');
  const catalogs = useCatalogs({ supplier_id: supplier.id });
  const [adding, setAdding] = useState(false);
  const items = catalogs.data?.items ?? [];

  return (
    <Card
      title="Catálogos"
      aside={
        manage ? (
          <Button
            variant="secondary"
            size="sm"
            icon={<UploadIcon size={14} />}
            onClick={() => setAdding(true)}
          >
            Subir catálogo
          </Button>
        ) : undefined
      }
    >
      {catalogs.isPending ? (
        <Spinner label="Cargando los catálogos" />
      ) : catalogs.isError ? (
        <ErrorMessage error={catalogs.error}>No se pudieron cargar los catálogos</ErrorMessage>
      ) : items.length === 0 ? (
        <p className="text-ink-3 text-[14px]">
          {manage
            ? 'Sube el PDF tal como lo manda el proveedor. Se abre aquí y en el móvil sin bajarlo entero.'
            : 'Este proveedor no tiene catálogos todavía.'}
        </p>
      ) : (
        <ul className="divide-line flex flex-col divide-y">
          {items.map((catalog) => (
            <CatalogRow key={catalog.id} catalog={catalog} manage={manage} />
          ))}
        </ul>
      )}
      {adding ? <UploadDialog supplier={supplier} onDone={() => setAdding(false)} /> : null}
    </Card>
  );
}

/** «Catálogo» finds «catalogo»: without accents or capitals. */
function fold(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/** More than this and the shelf gets a box to filter it. */
const SHELF_FILTER_FROM = 6;

/**
 * The catalogs ready to open, of every supplier, at the top of «Documentos»: what the
 * salesperson opens on the phone (meetings of 08/09 and 07/10). The ones still preparing
 * or failed are only in their supplier's file, for whoever manages them.
 */
export function CatalogShelf() {
  const catalogs = useCatalogs({}, { poll: false });
  const [filter, setFilter] = useState('');
  const ready = (catalogs.data?.items ?? []).filter((each) => each.status === 'ready');
  if (catalogs.isError) {
    return <ErrorMessage error={catalogs.error}>No se pudieron cargar los catálogos</ErrorMessage>;
  }
  if (ready.length === 0) return null;
  const words = fold(filter).split(/\s+/).filter(Boolean);
  const shown = ready.filter((each) => {
    const haystack = fold(`${each.name} ${each.supplier_name} ${each.filename}`);
    return words.every((word) => haystack.includes(word));
  });

  return (
    <section aria-label="Catálogos" className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-ink text-[15px] font-semibold">Catálogos</h2>
        {ready.length > SHELF_FILTER_FROM ? (
          <div className="relative w-full sm:w-72">
            <SearchIcon
              size={16}
              className="text-ink-4 pointer-events-none absolute top-1/2 left-3 -translate-y-1/2"
            />
            <input
              type="search"
              aria-label="Filtrar los catálogos"
              placeholder="Proveedor o catálogo"
              className="field w-full pl-9"
              value={filter}
              onChange={(event) => setFilter(event.target.value)}
            />
          </div>
        ) : null}
      </div>
      {shown.length === 0 ? (
        <p className="text-ink-3 text-[13.5px]">Ningún catálogo con «{filter.trim()}».</p>
      ) : (
        <CardList label="Catálogos" className="sm:grid sm:grid-cols-2 xl:grid-cols-3">
          {shown.map((catalog) => (
            <CardListItem
              key={catalog.id}
              to={catalogPath(catalog)}
              title={catalog.name}
              subtitle={`${catalog.supplier_name} · ${catalogFacts(catalog)}`}
            />
          ))}
        </CardList>
      )}
    </section>
  );
}

function CatalogRow({ catalog, manage }: { catalog: Catalog; manage: boolean }) {
  const [renaming, setRenaming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const retry = useRetryCatalog(catalog.id);
  const ready = catalog.status === 'ready';

  return (
    <li className="flex flex-col gap-2 py-3 first:pt-0 last:pb-0">
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
        <div className="flex min-w-0 items-start gap-2.5">
          <FileTextIcon size={18} className="text-ink-3 mt-0.5 shrink-0" />
          <div className="min-w-0">
            {ready ? (
              <Link className="link text-[14.5px] font-medium" to={catalogPath(catalog)}>
                {catalog.name}
              </Link>
            ) : (
              <p className="text-ink text-[14.5px] font-medium">{catalog.name}</p>
            )}
            <p className="text-ink-3 text-[13px]">
              {ready ? catalogFacts(catalog) : formatBytes(catalog.size ?? 0)}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-1">
          {catalog.status === 'preparing' ? (
            <Badge tone={catalog.stuck ? 'danger' : 'warn'}>
              {catalog.stuck ? 'Atascado' : CATALOG_STATUS_LABELS.preparing}
            </Badge>
          ) : null}
          {catalog.status === 'failed' ? (
            <Badge tone="danger">{CATALOG_STATUS_LABELS.failed}</Badge>
          ) : null}
          {manage ? (
            <>
              <Button
                variant="ghost"
                size="sm"
                aria-label={`Cambiar el nombre de ${catalog.name}`}
                onClick={() => setRenaming(true)}
              >
                Cambiar el nombre
              </Button>
              <Button
                variant="ghost"
                size="sm"
                aria-label={`Borrar ${catalog.name}`}
                onClick={() => setDeleting(true)}
              >
                Borrar
              </Button>
            </>
          ) : null}
        </div>
      </div>

      {catalog.status === 'failed' ? (
        <p className="text-danger text-[13.5px]">
          {catalogFailureTitle(catalog.error_code)}. {catalog.error ?? ''}{' '}
          {catalog.error_code ? (
            <span className="text-ink-3 text-[12.5px]">({catalog.error_code})</span>
          ) : null}
        </p>
      ) : null}
      {catalog.status === 'preparing' && catalog.stuck ? (
        <p className="text-ink-2 text-[13.5px]">Lleva demasiado tiempo preparándose.</p>
      ) : null}
      {manage && catalog.can_retry ? (
        <div>
          <Button size="sm" disabled={retry.isPending} onClick={() => retry.mutate()}>
            {retry.isPending ? 'Pidiéndolo…' : 'Volver a intentarlo'}
          </Button>
        </div>
      ) : null}
      {retry.isError ? (
        <ErrorMessage error={retry.error}>No se pudo volver a pedir</ErrorMessage>
      ) : null}

      {renaming ? <RenameDialog catalog={catalog} onDone={() => setRenaming(false)} /> : null}
      {deleting ? <DeleteDialog catalog={catalog} onDone={() => setDeleting(false)} /> : null}
    </li>
  );
}

function UploadDialog({ supplier, onDone }: { supplier: Supplier; onDone: () => void }) {
  const upload = useUploadCatalog(supplier.id);
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState('');
  const tooBig = file !== null && file.size > MAX_BYTES;
  const notPdf = file !== null && !isPdfFile(file);
  const busy = upload.isPending;
  const share = upload.progress?.total
    ? Math.min(1, upload.progress.loaded / upload.progress.total)
    : 0;
  // Every byte is in the bucket: what is left is telling the API, and a cancel then would
  // only lose the answer, not the catalog.
  const sent = busy && upload.progress !== null && upload.progress.loaded >= upload.progress.total;
  // A cancel is not a failure to show: the dialog stays open with the file chosen.
  const failed = upload.isError && !isAbort(upload.error);

  return (
    <Dialog
      title={`Subir un catálogo de ${supplier.name}`}
      description="El PDF tal como lo manda el proveedor, hasta 500 MB."
      // While it goes up, a click beside it or Escape does nothing: half an upload is lost
      // by a slip. «Cancelar la subida» says what it does.
      busy={busy}
      onClose={onDone}
      footer={
        <>
          <Button
            variant="secondary"
            disabled={sent}
            onClick={() => {
              if (busy) upload.cancel();
              else onDone();
            }}
          >
            {busy ? 'Cancelar la subida' : 'Cancelar'}
          </Button>
          <Button
            disabled={!file || tooBig || notPdf || busy}
            onClick={() => {
              if (!file) return;
              upload.mutate({ file, name: name.trim() || null }, { onSuccess: onDone });
            }}
          >
            {busy ? 'Subiendo…' : 'Subir'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <label className="flex flex-col gap-1 text-[13.5px]">
          <span className="text-ink-2 font-medium">Fichero</span>
          <input
            type="file"
            accept={CATALOG_ACCEPT}
            disabled={busy}
            className="text-ink-2 text-[13.5px]"
            onChange={(event) => {
              const chosen = event.target.files?.[0] ?? null;
              setFile(chosen);
              if (chosen && !name.trim()) setName(nameFromFile(chosen.name));
            }}
          />
          {file ? (
            <span className="text-ink-3 text-[12.5px]">
              {file.name} · {formatBytes(file.size)}
            </span>
          ) : null}
        </label>
        {tooBig ? (
          <p role="alert" className="text-danger text-[13.5px]">
            Pesa más de 500 MB: no se puede subir.
          </p>
        ) : null}
        {notPdf ? (
          <p role="alert" className="text-danger text-[13.5px]">
            Tiene que ser un PDF.
          </p>
        ) : null}
        <Input
          label="Nombre del catálogo"
          value={name}
          maxLength={200}
          disabled={busy}
          onChange={(event) => setName(event.target.value)}
          hint="Como lo buscará el equipo: «Catálogo general 2026», «Herramienta eléctrica»."
        />
        {busy ? (
          <div className="flex flex-col gap-1.5" aria-live="polite">
            <div
              role="progressbar"
              aria-label="Subida del catálogo"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(share * 100)}
              className="bg-paper-2 h-2 overflow-hidden rounded-full"
            >
              <div
                className="bg-accent h-full transition-[width] duration-300"
                style={{ width: `${Math.round(share * 100)}%` }}
              />
            </div>
            <p className="text-ink-3 text-[12.5px]">
              {sent && share > 0
                ? 'Comprobando el fichero…'
                : `${formatBytes(upload.progress?.loaded ?? 0)} de ${formatBytes(file?.size ?? 0)}. No cierres esta ventana.`}
            </p>
          </div>
        ) : null}
        {failed ? (
          <ErrorMessage error={upload.error}>No se pudo subir el catálogo</ErrorMessage>
        ) : null}
      </div>
    </Dialog>
  );
}

function RenameDialog({ catalog, onDone }: { catalog: Catalog; onDone: () => void }) {
  const rename = useRenameCatalog(catalog.id);
  const [name, setName] = useState(catalog.name);
  const trimmed = name.trim();

  return (
    <Dialog
      title="Cambiar el nombre del catálogo"
      onClose={onDone}
      footer={
        <>
          <Button variant="secondary" onClick={onDone} disabled={rename.isPending}>
            Cancelar
          </Button>
          <Button
            disabled={!trimmed || trimmed === catalog.name || rename.isPending}
            onClick={() => rename.mutate(trimmed, { onSuccess: onDone })}
          >
            {rename.isPending ? 'Guardando…' : 'Guardar'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Input
          label="Nombre"
          value={name}
          maxLength={200}
          onChange={(event) => setName(event.target.value)}
        />
        {rename.isError ? (
          <ErrorMessage error={rename.error}>No se pudo cambiar el nombre</ErrorMessage>
        ) : null}
      </div>
    </Dialog>
  );
}

function DeleteDialog({ catalog, onDone }: { catalog: Catalog; onDone: () => void }) {
  const remove = useDeleteCatalog(catalog.id);
  return (
    <Dialog
      title="¿Borrar este catálogo?"
      description={`«${catalog.name}» se borra del almacén y nadie podrá abrirlo. Las tarifas del proveedor no cambian.`}
      onClose={onDone}
      footer={
        <>
          <Button variant="secondary" onClick={onDone} disabled={remove.isPending} data-autofocus>
            Cancelar
          </Button>
          <Button
            variant="danger"
            disabled={remove.isPending}
            onClick={() => remove.mutate(undefined, { onSuccess: onDone })}
          >
            {remove.isPending ? 'Borrando…' : 'Borrar'}
          </Button>
        </>
      }
    >
      {remove.isError ? (
        <ErrorMessage error={remove.error}>No se pudo borrar el catálogo</ErrorMessage>
      ) : null}
    </Dialog>
  );
}
